import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { FinanceApiService } from '../finance/finance-api.service';
import { formatCustomerReferenceFromPlatform } from './catalog-customer.util';
import {
  CatalogOffering,
  CatalogService,
  MarketplaceSeller,
  UnifiedCommerceOrder
} from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-unified-commerce-orders',
  imports: [
    AinPageComponent, AinStateComponent, TableModule, ButtonModule, DatePipe, DecimalPipe,
    DialogModule, FormsModule, InputTextModule, SelectModule, CheckboxModule, CustomerSelectorComponent
  ],
  template: `
    <ain-page [title]="t('catalog.commerce.orders.title')" [description]="t('catalog.commerce.orders.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.commerce.orders.create')" (click)="openCreate()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" (click)="load()"></button>
          </div>
          @if (message()) { <p class="message">{{ message() }}</p> }
          <p-table [value]="orders()" [paginator]="true" [rows]="15">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.commerce.orders.customer') }}</th>
                <th>{{ t('catalog.tracking.status') }}</th>
                <th>{{ t('catalog.commerce.orders.channel') }}</th>
                <th>{{ t('catalog.commerce.orders.amount') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.customerReference }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.fulfillmentChannel }}</td>
                <td>{{ row.grossAmount | number:'1.2-2' }} {{ row.currencyCode }}</td>
                <td>
                  @if (row.status === 'Created' || row.status === 'PaymentCaptured' || row.status === 'Fulfilled') {
                    <button pButton type="button" size="small" [label]="t('catalog.commerce.orders.checkout')" [disabled]="acting()" (click)="checkout(row.id)"></button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }

      <p-dialog [header]="t('catalog.commerce.orders.create')" [(visible)]="createVisible" [modal]="true" [style]="{ width: '480px' }">
        <div class="form">
          <label>{{ t('catalog.commerce.orders.offering') }}</label>
          <p-select [options]="offeringOptions()" optionLabel="label" optionValue="value" [(ngModel)]="formOfferingId" [style]="{ width: '100%' }" />
          <label>{{ t('catalog.commerce.orders.customer') }}</label>
          <ain-customer-selector [(ngModel)]="formCustomerId" />
          <label>{{ t('catalog.commerce.orders.amount') }}</label>
          <input pInputText type="number" [(ngModel)]="formAmount" class="full" />
          @if (!selectedOfferingDigital()) {
            <label>{{ t('catalog.commerce.orders.seller') }}</label>
            <p-select [options]="sellerOptions()" optionLabel="label" optionValue="value" [(ngModel)]="formSellerId" [style]="{ width: '100%' }" />
          }
          <div class="check-row">
            <p-checkbox [(ngModel)]="formPostFinancial" [binary]="true" inputId="postFin" />
            <label for="postFin">{{ t('catalog.commerce.orders.postFinancial') }}</label>
          </div>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createVisible = false"></button>
          <button pButton type="button" [label]="t('catalog.action.create')" [disabled]="acting()" (click)="createOrder()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 12px; }
    .toolbar { display: flex; gap: 8px; }
    .message { margin: 0; color: #166534; font-weight: 600; }
    .form { display: grid; gap: 8px; }
    .full { width: 100%; }
    .check-row { display: flex; align-items: center; gap: 8px; }
  `]
})
export class UnifiedCommerceOrdersComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly financeApi = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly acting = signal(false);
  readonly orders = signal<UnifiedCommerceOrder[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly sellers = signal<MarketplaceSeller[]>([]);
  readonly message = signal('');

  createVisible = false;
  formOfferingId = '';
  formCustomerId: string | null = null;
  formAmount = 100;
  formSellerId = '';
  formPostFinancial = false;
  private pendingCheckoutPostFinancial = false;

  ngOnInit(): void {
    this.service.listOfferings().subscribe({ next: o => this.offerings.set(o) });
    this.service.listMarketplaceSellers().subscribe({ next: s => this.sellers.set(s) });
    this.load();
  }

  offeringOptions = () => this.offerings().map(o => ({ label: `${o.code} — ${o.title}`, value: o.id }));
  sellerOptions = () => this.sellers().filter(s => s.status === 'Approved').map(s => ({ label: s.displayName, value: s.id }));
  selectedOfferingDigital = () => this.offerings().find(o => o.id === this.formOfferingId)?.digitalDeliveryEnabled ?? false;

  load(): void {
    this.loading.set(true);
    this.service.listUnifiedCommerceOrders().subscribe({
      next: rows => { this.orders.set(rows); this.loading.set(false); },
      error: () => { this.orders.set([]); this.loading.set(false); }
    });
  }

  openCreate(): void {
    this.formOfferingId = this.offerings()[0]?.id ?? '';
    this.formCustomerId = null;
    this.formAmount = 100;
    this.formSellerId = this.sellers()[0]?.id ?? '';
    this.createVisible = true;
  }

  createOrder(): void {
    if (!this.formOfferingId || !this.formCustomerId) {
      this.message.set(this.t('catalog.customer.required'));
      return;
    }

    this.acting.set(true);
    this.pendingCheckoutPostFinancial = this.formPostFinancial;
    const key = `commerce-${Date.now()}`;
    const customerId = this.formCustomerId;
    this.financeApi.getPlatformCustomer(customerId).subscribe({
      next: customer => {
        this.service.createUnifiedCommerceOrder({
          offeringId: this.formOfferingId,
          customerId,
          customerReference: formatCustomerReferenceFromPlatform(customer),
          grossAmount: this.formAmount,
          idempotencyKey: key,
          marketplaceSellerId: this.selectedOfferingDigital() ? null : (this.formSellerId || null)
        }).subscribe({
          next: order => {
            this.createVisible = false;
            this.checkout(order.id);
          },
          error: () => this.acting.set(false)
        });
      },
      error: () => this.acting.set(false)
    });
  }

  checkout(id: string): void {
    this.acting.set(true);
    this.service.checkoutUnifiedCommerceOrder(id, this.pendingCheckoutPostFinancial, 100).subscribe({
      next: () => {
        this.message.set(this.t('catalog.commerce.orders.checkoutDone'));
        this.load();
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  t(key: string): string { return this.localization.translate(key); }
}
